import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import { LOCAL_BUSINESS_SCHEMAS } from "../../components/seo/schemas";
import {
  ArrowRight, ArrowUpRight, Sparkles, ShieldCheck, Stethoscope,
  Handshake, BadgeCheck, Scale, Building2, MapPin, Phone, Mail,
  BarChart3, Users, Globe2, Compass, Award, CheckCircle2,
} from "lucide-react";

/* ---------------- Data ---------------- */

const TRUST_LOGOS = [
  { src: "/certifications/nmsdc.png",  alt: "NMSDC MBE"  },
  { src: "/certifications/nctrca.png", alt: "NCTRCA"     },
  { src: "/certifications/sam.png",    alt: "SAM.gov"    },
  { src: "/certifications/mbe.png",    alt: "NCTRCA MBE" },
  { src: "/certifications/sbe.jpeg",   alt: "NCTRCA SBE" },
];

const FACTS = [
  { k: "Healthcare-only",    d: "Zero non-medical verticals — ever." },
  { k: "Texas HQ · India ops", d: "Missouri City · Stafford · Noida." },
  { k: "4 personas served",  d: "Clinicians · Facilities · Recruiters · Admins." },
];

const POSITIONING = [
  {
    Icon: Compass,
    t: "Operator DNA",
    d: "Our team combines acute-care staffing operators, MSP program leads, and enterprise SaaS engineers — we build what we'd want if we were staffing the floor at 03:00.",
  },
  {
    Icon: ShieldCheck,
    t: "Compliance-first",
    d: "Every workflow begins with primary-source verification and Joint Commission-aligned documentation. Compliance is the default state, not a feature toggle.",
  },
  {
    Icon: Scale,
    t: "Procurement-ready",
    d: "Tier-1 diverse-spend reporting, named executive sponsors, and single-page QBRs that put clinical quality, fill-rate, and exceptions on one sheet of paper.",
  },
];

const DIFFERENTIATORS = [
  { Icon: Stethoscope, t: "Clinical quality at parity",
    d: "Same credentialing depth, same SLAs, same outcomes as any prime on the panel — we never ask a client to trade quality for diversity spend." },
  { Icon: BadgeCheck, t: "Multi-authority certified",
    d: "NMSDC + HMSDC Minority Business Enterprise, NCTRCA dual MBE/SBE, and SAM.gov-registered federal contractor. One vendor, every authority." },
  { Icon: ShieldCheck, t: "Audit-ready, every day",
    d: "Credential files, exception logs, and competency attestations kept in a Joint-Commission-aligned format — ready for on-demand survey pull." },
  { Icon: BarChart3, t: "Pay transparency by default",
    d: "Every shift posted to clinicians shows bill-rate logic, stipend breakdown, and travel-reimbursement math. No hidden spread, no re-trade surprises." },
  { Icon: Handshake, t: "Named executive sponsor",
    d: "One accountable exec per enterprise account. You get a name, a mobile number, and a one-page QBR — never a ticket queue." },
  { Icon: Globe2, t: "Specific without being small",
    d: "24/7 recruiter coverage across USA + India operations means we scale fill capacity without losing the specificity that makes clinical staffing work." },
];

const PRINCIPLES = [
  { n: "01", t: "Tell the truth early.",
    d: "We surface bad news — fill-risk, no-shows, credential lapses — the moment we see it, not at QBR. Every week, not every quarter." },
  { n: "02", t: "One-page quarterly reviews.",
    d: "Diverse spend, clinical quality exceptions, fill-rate trends, and open risks on a single sheet procurement can take straight into board review." },
  { n: "03", t: "No surprise line items.",
    d: "Pricing, OT triggers, cancellation windows, and float clauses are agreed once, written down, and never re-litigated mid-assignment." },
  { n: "04", t: "Escalate patterns, not tickets.",
    d: "A cluster of three similar issues becomes a workflow change on our end — not three angry emails on yours." },
];

const STATS = [
  { k: "100%",   d: "Healthcare-only bookings" },
  { k: "24 / 7", d: "Recruiter + compliance coverage" },
  { k: "2 + 1",  d: "USA offices · India operations hub" },
  { k: "SOC-2",  d: "Attested credentialing platform" },
];

const OFFICES = [
  { label: "USA · Head Office",
    lines: ["5423 Pecan Fall PL", "Missouri City, TX 77459"] },
  { label: "USA · Corporate",
    lines: ["11104 West Airport Blvd, Suite 213", "Stafford, TX 77477"] },
  { label: "India · Operations",
    lines: ["First floor, A-83, A Block, Sector 63", "Noida, UP 201309"] },
];

/* ---------------- Page ---------------- */

export default function About() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <PageMeta
        title="About TRITAL Care® — Healthcare-only workforce partner"
        description="TRITAL Care® (operating brand of TOPTAL Care Inc.) is a healthcare-only clinical workforce partner — NMSDC/HMSDC MBE-certified, NCTRCA dual MBE/SBE, and SAM.gov registered. Texas-headquartered with 24/7 operations across USA and India."
        canonical="https://www.tritalcare.com/about"
        schema={LOCAL_BUSINESS_SCHEMAS}
      />
      <PublicNav />

      {/* 1 · HERO ----------------------------------------------- */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="about-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-navy">About TRITAL Care<sup>®</sup></span>
            </div>
            <h1 className="mt-6 font-display font-black text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-slate-900 text-balance">
              Built for the floor.
              <span className="block text-trital-navy">Engineered for the enterprise.</span>
            </h1>
            <p className="mt-6 text-slate-600 max-w-2xl leading-relaxed text-lg">
              <span className="font-semibold text-slate-900">TRITAL Care<sup>®</sup></span> is the operating brand of <span className="font-semibold text-slate-900">TOPTAL Care Inc.</span> — a healthcare-only clinical workforce partner serving hospitals, health systems, skilled-nursing operators and managed service programs. We close the gap between floor-level staffing reality and enterprise procurement accountability.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep"
                data-testid="about-hero-contact"
              >
                Talk to our team <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center px-6 py-3.5 rounded-full border border-slate-300 text-trital-navy font-semibold hover:bg-slate-50"
                data-testid="about-hero-services"
              >
                Browse services
              </Link>
            </div>
            <div className="mt-7 flex items-center gap-2.5 flex-wrap" data-testid="about-hero-trust">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Certified &amp; registered</span>
              <div className="flex items-center gap-1.5">
                {TRUST_LOGOS.map((l) => (
                  <img
                    key={l.src}
                    src={l.src}
                    alt={l.alt}
                    className="h-8 w-8 rounded-full bg-white border border-slate-200 object-contain p-0.5"
                    loading="lazy"
                  />
                ))}
              </div>
              <span className="text-[12px] text-slate-500">NMSDC · HMSDC · NCTRCA · SAM.gov</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
            className="lg:col-span-5"
          >
            <div className="rounded-3xl border border-slate-200 bg-white shadow-navy p-6 lg:p-7 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-trital-medical-blue-soft blur-3xl" />
              <div className="relative">
                <div className="overline text-trital-red">At a glance</div>
                <div className="mt-4 grid grid-cols-1 gap-3">
                  {FACTS.map(({ k, d }) => (
                    <div key={k} className="rounded-xl border border-slate-200 bg-white p-3.5 flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-trital-navy text-white flex items-center justify-center shrink-0">
                        <CheckCircle2 className="w-4.5 h-4.5" strokeWidth={1.8} />
                      </div>
                      <div>
                        <div className="font-display font-black text-[15px] text-trital-navy leading-tight">{k}</div>
                        <div className="mt-0.5 text-[12.5px] text-slate-600 leading-snug">{d}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
                  <div className="overline text-slate-500">Legal entity</div>
                  <div className="mt-1 text-[13px] text-slate-700 leading-relaxed">
                    TRITAL Care<sup>®</sup> is a registered service mark and the operating brand name of <span className="font-semibold text-slate-900">TOPTAL Care Inc.</span>, a Texas corporation focused on clinical workforce infrastructure.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2 · COMPANY INTRODUCTION ------------------------------ */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="about-intro">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline text-trital-red">The company</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
              A workforce partner built for how healthcare actually operates.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-slate-600 leading-relaxed text-[15.5px]">
            <p>
              We were founded to close the operational gap between hospital scheduling requirements and the clinician supply available to meet them — without sacrificing the compliance, audit, and pay-transparency defaults procurement teams expect of a modern vendor.
            </p>
            <p>
              Our team is assembled from acute-care staffing operators, managed service program leads, and enterprise SaaS engineers. We build the platform we would want to run if we were staffing the floor at 3:00 a.m. — and we sell it the way procurement would want to buy it.
            </p>
            <p>
              We serve hospitals, health systems, skilled-nursing operators, and the managed service providers who coordinate across them — alongside the nurses, allied professionals, and physicians who take the assignments. Our only vertical is healthcare. Our only mandate is clinical quality at parity with the largest prime vendor on the panel.
            </p>
          </div>
        </div>
      </section>

      {/* 3 · MISSION AND POSITIONING --------------------------- */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden" data-testid="about-mission">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-85 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <div className="overline text-white/60">Mission &amp; positioning</div>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-white text-balance">
                Close the gap between
                <span className="block text-trital-red">the floor and the P&amp;L.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 rounded-3xl bg-white/5 border border-white/10 backdrop-blur p-7 lg:p-9">
              <div className="overline text-trital-red">Our mission</div>
              <p className="mt-3 text-white/90 text-lg lg:text-[22px] font-display font-medium leading-[1.35] text-balance">
                Put clinically-excellent workforce supply exactly where and when healthcare needs it —
                <span className="text-white"> with the compliance, transparency, and accountability the enterprise requires.</span>
              </p>
              <div className="mt-6 flex items-center gap-2 text-[12.5px] text-white/70">
                <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
                Triage Precision · Trusted Workforce
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {POSITIONING.map(({ Icon, t, d }, i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6"
                data-testid={`about-positioning-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-white text-trital-navy flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="mt-5 font-display font-bold text-[17px] text-white">{t}</div>
                <p className="mt-2 text-[13.5px] text-white/75 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · DIFFERENTIATORS ----------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="about-differentiators">
        <div className="max-w-3xl">
          <div className="overline text-trital-red flex items-center gap-1.5">
            <Award className="w-3 h-3" strokeWidth={1.8} /> What makes TRITAL Care different
          </div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
            Not another staffing vendor. <span className="text-trital-navy">A clinical-workforce utility.</span>
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
            Six things you won't find bundled together anywhere else in healthcare workforce — and the ones that show up on your scorecard when procurement reviews vendor panels.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {DIFFERENTIATORS.map(({ Icon, t, d }, i) => (
            <motion.div
              key={t}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.35, delay: i * 0.04 }}
              className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all"
              data-testid={`about-diff-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center group-hover:bg-trital-navy group-hover:text-white transition-colors">
                <Icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="mt-5 font-display font-bold text-[17px] leading-snug text-slate-900">{t}</div>
              <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5 · PARTNERSHIP MINDSET ------------------------------- */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="about-partnership">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline text-trital-red">Partnership mindset</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
              How we show up,
              <span className="block text-trital-navy">every week.</span>
            </h2>
            <p className="mt-4 text-slate-600 max-w-xl leading-relaxed">
              Vendor relationships fail on operating cadence long before they fail on contract language. These four principles govern how our account teams work with your procurement, clinical leadership, and scheduling office — week in, week out.
            </p>
            <div className="mt-6 flex items-center gap-2 text-[12.5px] text-slate-500">
              <Handshake className="w-4 h-4 text-trital-navy" strokeWidth={1.8} />
              One accountable executive · one mobile number · one-page QBR.
            </div>
          </div>
          <ul className="lg:col-span-7 grid md:grid-cols-2 gap-4" data-testid="about-principles">
            {PRINCIPLES.map(({ n, t, d }, i) => (
              <motion.li
                key={n}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="rounded-2xl bg-white border border-slate-200 p-6 shadow-card relative overflow-hidden"
                data-testid={`about-principle-${n}`}
              >
                <div className="absolute -top-2 -right-3 font-display font-black text-[64px] text-trital-navy/[0.06] leading-none select-none">{n}</div>
                <div className="relative">
                  <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-trital-red">
                    <span className="w-6 h-6 rounded-full bg-trital-red text-white flex items-center justify-center text-[10px]">{n}</span>
                    Principle
                  </div>
                  <div className="mt-3 font-display font-bold text-[17px] text-slate-900">{t}</div>
                  <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{d}</p>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* 6 · LEADERSHIP / COMPANY SNAPSHOT --------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="about-snapshot">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="overline text-trital-red">Leadership &amp; company snapshot</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
              A disciplined team, across three offices.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate-600 leading-relaxed">
              Led by a bench of healthcare-staffing operators, compliance leads, and platform engineers — coordinating 24/7 across Texas and Noida.
            </p>
          </div>
        </div>

        {/* stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="about-stats">
          {STATS.map(({ k, d }, i) => (
            <motion.div
              key={k}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.04 }}
              className="rounded-2xl border border-slate-200 bg-white p-5 lg:p-6 shadow-card"
            >
              <div className="font-display font-black text-[28px] lg:text-[34px] text-trital-navy leading-none tracking-tight">{k}</div>
              <div className="mt-2 text-[12.5px] text-slate-600 leading-snug">{d}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6">
          {/* Philosophy quote */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl bg-trital-navy-ink text-white p-8 lg:p-10 relative overflow-hidden"
            data-testid="about-philosophy"
          >
            <div className="absolute inset-0 bg-trital-navy-mesh opacity-70 pointer-events-none" />
            <div className="relative">
              <div className="overline text-white/60">Operating philosophy</div>
              <blockquote className="mt-4 font-display font-medium text-[22px] lg:text-[28px] leading-[1.25] text-white text-balance">
                "The hospital doesn't need another vendor. It needs a utility: show up, stay on, carry load, tell the truth. We're building that — for healthcare, only."
              </blockquote>
              <div className="mt-6 flex items-center gap-3 text-white/70">
                <div className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center font-display font-bold text-white">TC</div>
                <div>
                  <div className="text-white font-semibold text-[14px]">TRITAL Care Leadership</div>
                  <div className="text-[11.5px] text-white/60">TOPTAL Care Inc. · Texas</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Offices */}
          <div className="rounded-3xl border border-slate-200 bg-white p-6 lg:p-8 shadow-card" data-testid="about-offices">
            <div className="overline text-trital-red">Offices</div>
            <ul className="mt-4 space-y-3">
              {OFFICES.map(({ label, lines }) => (
                <li key={label} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3.5">
                  <div className="w-9 h-9 rounded-lg bg-trital-navy text-white flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
                    <div className="mt-0.5 text-[13.5px] text-slate-800 leading-snug">
                      {lines.map((l) => <div key={l}>{l}</div>)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-5 border-t border-slate-100 grid sm:grid-cols-2 gap-2.5 text-[13px]">
              <a href="tel:+18323036622" className="inline-flex items-center gap-2 text-trital-navy font-semibold hover:underline" data-testid="about-phone">
                <Phone className="w-4 h-4" strokeWidth={1.8} /> +1 (832) 303-6622
              </a>
              <a href="mailto:info@tritalcare.com" className="inline-flex items-center gap-2 text-trital-navy font-semibold hover:underline" data-testid="about-email">
                <Mail className="w-4 h-4" strokeWidth={1.8} /> info@tritalcare.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 7 · CTA BAND ----------------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="about-cta-band">
        <div className="rounded-3xl bg-white border border-slate-200 p-8 lg:p-12 relative overflow-hidden shadow-card">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-trital-medical-blue-soft blur-3xl" />
          <div className="absolute -bottom-20 -left-16 w-72 h-72 rounded-full bg-red-100 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="overline text-trital-red">Start a conversation</div>
              <h3 className="mt-3 font-display font-black text-3xl lg:text-5xl tracking-tight text-slate-900 text-balance">
                Put a healthcare-only workforce partner on panel.
              </h3>
              <p className="mt-4 text-slate-600 max-w-xl leading-relaxed">
                Whether you're scoping a PRN pilot, running a formal RFP, or evaluating Tier-1 diverse-spend options — we'll bring the capability pack, a named exec sponsor, and a one-page QBR template to the first call.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-2 text-[12px] text-slate-500">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200">
                  <Building2 className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} /> Enterprise-ready · SOC-2 platform
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200">
                  <Users className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} /> 24/7 coverage · USA + India
                </span>
              </div>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/employers#request"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep"
                data-testid="about-cta-request"
              >
                Request talent <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link
                to="/capability-statement"
                className="inline-flex items-center px-6 py-3.5 rounded-full border border-slate-300 text-trital-navy font-semibold hover:bg-slate-50"
                data-testid="about-cta-capability"
              >
                Build a capability statement <ArrowUpRight className="w-4 h-4 ml-1" strokeWidth={1.8} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
